/* eslint-disable no-undefined */
// import BoostCommerceApi from '../api';
import {
  IActiveCollection, IGetProductsResultsQuery,
  IGetProductsResult,
  ISearchFilter,
  ISearchProviderConfig
} from '../../../types';
// import sortMap from '../helpers/sortMap';
import mapFilters from '../helpers/mappers/mapFilters';
import mapProduct from '../helpers/mappers/mapProduct';
import mapSort from '../helpers/mappers/mapSort';


import { elevate, localStorageBackedSession, SortType } from '@apptus/esales-api';
import {
  getLocale,
  getClusterId,
  getMarket,
  getTouchpoint,
  getViewId,
  getVoyadoSession,
  hasCookieConsent
} from '../helpers/voyadoHelpers';
import { elevateFetchShim } from '../helpers/elevateFetchShim';
import fetchContextualPrices from '../helpers/fetchContextualPrices';
import extenders from '../../../extenders';
import { FILTER_QUERY_PARAM_PREFIX, FILTER_QUERY_PARAM_SEPARATOR } from '../../../useSearch';
import { ISearchFilterOption, ISearchFilterRangeOption } from '../../../types';

/**
 * Convert searchFilters state into activeFilters format (same as URL params)
 * This allows us to use pending filter selections before they're applied to the URL
 */
const buildActiveFiltersFromState = (searchFilters: ISearchFilter[]): Record<string, string> => {
  const activeFilters: Record<string, string> = {};
  const multiOptionValues: Record<string, string[]> = {};

  if (!searchFilters || searchFilters.length === 0) {
    return activeFilters;
  }

  searchFilters.forEach((filter: ISearchFilter) => {
    filter.options.forEach(option => {
      if (filter.type === 'SINGLE_OPTION') {
        if (!option.selected) return;

        const optionValue = option as ISearchFilterOption;
        let filterValue: string = optionValue.value ? optionValue.value : optionValue.label;

        // The availability filter uses '1' or '0' as it's value instead of its label
        if (optionValue.identifier === 'In stock') filterValue = '1';
        if (optionValue.identifier === 'Out of stock') filterValue = '0';

        activeFilters[`f.${filter.identifier}`] = filterValue;
      } else if (filter.type === 'MULTI_OPTION') {
        if (!option.selected) return;

        const optionValue = option as ISearchFilterOption;
        const filterValue: string = optionValue.value ? optionValue.value : optionValue.label;

        if (!multiOptionValues[filter.identifier]) {
          multiOptionValues[filter.identifier] = [];
        }
        multiOptionValues[filter.identifier].push(filterValue);
      } else if (filter.type === 'RANGE') {
        const rangeOption = option as ISearchFilterRangeOption;
        activeFilters[`${FILTER_QUERY_PARAM_PREFIX}${filter.identifier}`] = `${rangeOption.selectedMinimum}:${rangeOption.selectedMaximum}`;
      }
    });
  });

  // Add all multi-option values to activeFilters
  Object.entries(multiOptionValues).forEach(([identifier, values]) => {
    activeFilters[`${FILTER_QUERY_PARAM_PREFIX}${identifier}`] = values.join(FILTER_QUERY_PARAM_SEPARATOR);
  });

  return activeFilters;
};

const GetProductResults = async (searchFilters: ISearchFilter[], activeCollection: IActiveCollection, query: IGetProductsResultsQuery, config: ISearchProviderConfig): Promise<IGetProductsResult> => {
  elevateFetchShim();

  // Initialize extenders
  extenders.forEach(extender => {
    if (extender.init) {
      extender.init();
    }
  });

  const pageTemplate = window['theme'].template;

  const mappedResponse: IGetProductsResult = {
    records: 0,
    total: 0,
    products: [],
    filters: [],
    sort: []
  }

  const pageIndex = query.pageIndex || 1;
  const limit = config.rProductsPerPage;
  const skip = (pageIndex - 1) * limit;

  // Build active filters: always use state (includes pending selections) for accurate pending counts
  // State is always up-to-date with user selections, while URL is only updated on Apply
  const activeFilters = {} as Record<string, string>;
  const searchStr = window.location.search || '';

  // Build from state first (includes pending filter selections)
  if (searchFilters && searchFilters.length > 0) {
    const stateFilters = buildActiveFiltersFromState(searchFilters);
    Object.assign(activeFilters, stateFilters);
  }

  // Only use URL params if state has no filter selections
  // This handles initial page load when state might not be populated yet
  if (Object.keys(activeFilters).length === 0 && (searchStr.includes('filter_') || searchStr.includes('f.'))) {
    const params = new URLSearchParams(searchStr);
    params.forEach((value, key) => {
      if (!key || value === null || value === undefined) return;
      if (key.startsWith('filter_') || key.startsWith('f.')) {
        activeFilters[key] = decodeURIComponent(value);
      }
    });
  }

  const sortQuery = new URLSearchParams(window.location.search).get('sort') as SortType;
  let sortKey: SortType | null = null;

  if (sortQuery && sortQuery !== null) {
    sortKey = sortQuery;
  }


  try {
    // Step 1: Ensure DOM is ready and config element exists
    const configElement = document.querySelector('[data-app-layer-config-search]');
    if (!configElement) {
      console.warn('Config DOM element not found, waiting for DOM...');
      // Wait a bit for DOM to be ready
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Step 2: Validate all required config values are available
    const market = getMarket();
    const locale = getLocale();
    const touchpoint = getTouchpoint();
    const clusterId = getClusterId();

    // Step 3: Ensure session is properly initialized and keys are generated
    // Use ephemeral session (random per request) if cookies declined, otherwise persisted session
    const session = getVoyadoSession(localStorageBackedSession);
    const sessionKeys = session();

    const hasConsent = hasCookieConsent();

    // Step 4: Validate all required values exist before creating API instance
    if (!clusterId) {
      throw new Error('Cluster ID is missing from config');
    }
    if (!market) {
      throw new Error('Market is missing');
    }
    if (!locale) {
      throw new Error('Locale is missing');
    }
    if (!sessionKeys.customerKey || !sessionKeys.sessionKey) {
      throw new Error('Session keys are not properly initialized');
    }

    // Step 5: Create API instance with validated config
    const api = elevate({
      clusterId,
      market,
      locale,
      touchpoint,
      session: session
    });



    let res

    switch (pageTemplate) {
      case 'search': {
        // Making Voyado searchPage API call

        // Transform filters to Voyado facet format
        const voyadoFilters: any = {};

        Object.keys(activeFilters).forEach(key => {
          const value = activeFilters[key];

          if (key.startsWith('f.')) {
            // Transform the filter format to Voyado facet format
            if (key === 'f.color') {
              // Try using facets parameter: facets={color:[value]}
              if (!voyadoFilters.facets) voyadoFilters.facets = {};
              voyadoFilters.facets.color = [value];
            } else if (key === 'f.price.min') {
              if (!voyadoFilters.facets) voyadoFilters.facets = {};
              if (!voyadoFilters.facets.price) voyadoFilters.facets.price = {};
              voyadoFilters.facets.price.min = value;
            } else if (key === 'f.price.max') {
              if (!voyadoFilters.facets) voyadoFilters.facets = {};
              if (!voyadoFilters.facets.price) voyadoFilters.facets.price = {};
              voyadoFilters.facets.price.max = value;
            } else {
              // Handle other filter types by removing 'f.' prefix
              const attribute = key.replace('f.', '');
              if (!voyadoFilters.facets) voyadoFilters.facets = {};
              voyadoFilters.facets[attribute] = [value];
            }
          } else if (key.startsWith('filter_')) {
            // Handle multi/single options and range encoded as filter_{identifier}
            const identifier = key.replace('filter_', '');
            if (identifier === 'price') {
              // Expect value like min:max
              let [minStr, maxStr] = (value || '').split(':');

              // Reverse conversion for price filters to send base GBP values to Voyado
              const currencyRate = parseFloat(window.Shopify?.currency?.rate) || 1;
              const isB2B = window.theme?.isb2b === true;
              const b2bMultiplier = 0.4;
              const combinedMultiplier = isB2B ? (currencyRate * b2bMultiplier) : currencyRate;

              if (combinedMultiplier !== 1) {
                if (minStr !== undefined && minStr !== '') minStr = (Math.floor((parseFloat(minStr) / combinedMultiplier) * 100) / 100).toString();
                if (maxStr !== undefined && maxStr !== '') maxStr = (Math.ceil((parseFloat(maxStr) / combinedMultiplier) * 100) / 100).toString();
              }

              if (!voyadoFilters.facets) voyadoFilters.facets = {};
              if (!voyadoFilters.facets.price) voyadoFilters.facets.price = {};
              if (minStr !== undefined && minStr !== '') voyadoFilters.facets.price.min = minStr;
              if (maxStr !== undefined && maxStr !== '') voyadoFilters.facets.price.max = maxStr;
            } else {
              // Multi-values can be separated by '|' or ',' depending on cluster/config
              const values = (value || '')
                .split(/\||,/)
                .filter(v => v !== '');
              if (!voyadoFilters.facets) voyadoFilters.facets = {};
              voyadoFilters.facets[identifier] = values.length ? values : [value];
            }
          } else {
            // Preserve any other params as-is
            voyadoFilters[key] = activeFilters[key];
          }
        });

        // Build the API parameters, excluding invalid sort values
        // Note: q must be set AFTER spreading voyadoFilters to prevent URL params from overriding the corrected query term
        const apiParams: {filter?: string, viewId?: any, q: string, limit?: number, skip?: number, sort?: SortType, notify?: boolean} = {
          viewId: getViewId(),
          limit: limit,
          skip: skip,
          ...voyadoFilters,
          q: query.term ?? new URLSearchParams(window.location.search).get('q') ?? ''
        };

        // Forward Voyado phrase_suggest for searches initiated via suggested phrases
        const phraseSuggestParam = new URLSearchParams(window.location.search).get('phrase_suggest');
        if (phraseSuggestParam && phraseSuggestParam !== '0' && phraseSuggestParam !== 'false') {
          (apiParams as any).origin = 'PHRASE_SUGGEST';
        }

        // Only include notify when cookies are declined (notify: false)
        // When cookies are accepted, omit notify to default to true
        if (!hasConsent) {
          (apiParams as any).notify = false;
        }

        // Only include sort if it's a valid value (not null, 'null', or empty)

        if (sortKey) {

          apiParams.sort = sortKey;
        }

        try {
          res = await api.query.searchPage(
            apiParams,
            {
              contentLists: [{ id: 'content-hits' }]
            }
          );

        } catch (apiError) {
          console.error('SearchPage API call failed:', {
            error: apiError,
            message: apiError?.message,
            stack: apiError?.stack,
            query: query.term ?? new URLSearchParams(window.location.search).get('q'),
            activeFilters
          });
          throw apiError;
        }

        // API call completed

        break;
      }
      case 'collection': {
        // Making Voyado landingPage API call

        // Transform filters to Voyado facet format
        const voyadoFilters: any = {};

        Object.keys(activeFilters).forEach(key => {
          const value = activeFilters[key];

          if (key.startsWith('f.')) {
            // Transform your filter format to Voyado facet format
            if (key === 'f.color') {
              // Try using facets parameter: facets={color:[value]}
              if (!voyadoFilters.facets) voyadoFilters.facets = {};
              voyadoFilters.facets.color = [value];
            } else if (key === 'f.price.min') {
              if (!voyadoFilters.facets) voyadoFilters.facets = {};
              if (!voyadoFilters.facets.price) voyadoFilters.facets.price = {};
              voyadoFilters.facets.price.min = value;
            } else if (key === 'f.price.max') {
              if (!voyadoFilters.facets) voyadoFilters.facets = {};
              if (!voyadoFilters.facets.price) voyadoFilters.facets.price = {};
              voyadoFilters.facets.price.max = value;
            } else {
              // Handle other filter types by removing 'f.' prefix
              const attribute = key.replace('f.', '');
              if (!voyadoFilters.facets) voyadoFilters.facets = {};
              voyadoFilters.facets[attribute] = [value];
            }
          } else if (key.startsWith('filter_')) {
            const identifier = key.replace('filter_', '');
            if (identifier === 'price') {
              let [minStr, maxStr] = (value || '').split(':');

              // Reverse conversion for price filters to send base GBP values to Voyado
              const currencyRate = parseFloat(window.Shopify?.currency?.rate) || 1;
              const isB2B = window.theme?.isb2b === true;
              const b2bMultiplier = 0.4;
              const combinedMultiplier = isB2B ? (currencyRate * b2bMultiplier) : currencyRate;

              if (combinedMultiplier !== 1) {
                if (minStr !== undefined && minStr !== '') minStr = (Math.floor((parseFloat(minStr) / combinedMultiplier) * 100) / 100).toString();
                if (maxStr !== undefined && maxStr !== '') maxStr = (Math.ceil((parseFloat(maxStr) / combinedMultiplier) * 100) / 100).toString();
              }

              if (!voyadoFilters.facets) voyadoFilters.facets = {};
              if (!voyadoFilters.facets.price) voyadoFilters.facets.price = {};
              if (minStr !== undefined && minStr !== '') voyadoFilters.facets.price.min = minStr;
              if (maxStr !== undefined && maxStr !== '') voyadoFilters.facets.price.max = maxStr;
            } else {
              const values = (value || '')
                .split(/\||,/)
                .filter(v => v !== '');
              if (!voyadoFilters.facets) voyadoFilters.facets = {};
              voyadoFilters.facets[identifier] = values.length ? values : [value];
            }
          } else {
            voyadoFilters[key] = activeFilters[key];
          }
        });

        // Build the API parameters, excluding invalid sort values
        const apiParams: {filter?: string, viewId?: any, pageReference?: string, limit?: number, skip?: number, sort?: SortType, notify?: boolean} = {
          viewId: getViewId(),
          pageReference: activeCollection ? `/${activeCollection.handle}` : '',
          limit: limit,
          skip: skip,
          ...voyadoFilters
        };

        // Only include sort if it's a valid value (not null, 'null', or empty)
        if (sortKey) {
          apiParams.sort = sortKey;
        }

        // Only include notify when cookies are declined (notify: false)
        // When cookies are accepted, omit notify to default to true
        if (!hasConsent) {
          (apiParams as any).notify = false;
        }

        try {
          res = await api.query.landingPage(
            apiParams,
            {
              primaryList: {
                include: true
              }
            }
          );

        } catch (apiError) {
          console.error('LandingPage API call failed:', {
            error: apiError,
            message: apiError?.message,
            stack: apiError?.stack,
            pageReference: activeCollection ? `${activeCollection.handle}` : '',
            activeFilters
          });
          throw apiError;
        }

        // API call completed

        break;
      }
      default:
        throw new Error('Error when getting Product results');
    }



    const { facets, productGroups, totalHits, sort } = res.primaryList;
    // Try accessing autoCorrect directly first, then destructure
    const resAny = res as any;
    const autoCorrect = resAny.autoCorrect;
    const { contentLists, didYouMean, q, secondaryList } = resAny;




    // Total is the total number of products returned after the filters applied, before pagination.
    // Records is the number of products on the page.
    mappedResponse.total = totalHits;
    mappedResponse.records = productGroups.length;
    mappedResponse.filters = mapFilters(facets);
    // Voyado returns an array of products within each productSuggestion, we only want the first
    // Helper to map products with contextual price data
    const productKeys = productGroups.map(group => group.products[0].key);
    let contextualPrices = {};

    // Only fetch contextual prices if we are NOT on the main GB site (or if specific config is set)
    // You might want to make this conditional based on theme config to avoid extra calls on base market
    const sfApiConfigStr = document.querySelector('[data-storefront-api-config]')?.textContent;
    let shouldFetchAdditionalData = false;

    if (sfApiConfigStr) {
      try {
        const parsedConfig = JSON.parse(sfApiConfigStr);
        // ALWAYS fetch if we have config, because we need tags for the sticker fallback
        // even if we don't strictly need contextual prices (GB)
        shouldFetchAdditionalData = !!parsedConfig;
      } catch(e) {
        // failed to parse config
      }
    }

    if (shouldFetchAdditionalData && productKeys.length > 0) {
      contextualPrices = await fetchContextualPrices(productKeys);
    }

    mappedResponse.products = productGroups?.flatMap((group, index) =>
      mapProduct(
        group.products[0],
        {
          index,
          pageIndex,
          pageMeta: (window as any).ShopifyAnalytics.meta.page,
          contextualPriceData: contextualPrices[group.products[0].key]
        }
      )
    );

    mappedResponse.sort = mapSort(sort);

    // If primary list is empty but secondary list exists, fallback to secondary in-place
    if ((totalHits === 0 || (productGroups?.length ?? 0) === 0) && secondaryList?.productGroups?.length) {
      const secGroups = secondaryList.productGroups as any[];
      const secTotal = secondaryList.totalHits as number;

      // Also fetch for secondary group if needed
      if (shouldFetchAdditionalData && secGroups.length > 0) {
        const secKeys = secGroups.map(g => g.products[0].key);
        const secContextualPrices = await fetchContextualPrices(secKeys);
        Object.assign(contextualPrices, secContextualPrices);
      }

      mappedResponse.products = secGroups.flatMap((group, index: number) =>
        mapProduct(
          group.products[0],
          {
            index,
            pageIndex,
            pageMeta: (window as any).ShopifyAnalytics.meta.page,
            contextualPriceData: contextualPrices[group.products[0].key]
          }
        )
      );
      mappedResponse.total = secTotal;
      mappedResponse.records = mappedResponse.products.length;
      mappedResponse.isSecondaryList = true;
    }
    // expose terms and corrections
    (mappedResponse as any).searchQuery = q; // e.g. "fdress"
    (mappedResponse as any).autoCorrectedQuery = autoCorrect?.q ?? null; // e.g. "dress"
    (mappedResponse as any).autoCorrectOriginalTotalHits = autoCorrect?.originalTotalHits ?? null;
    (mappedResponse as any).didYouMean = Array.isArray(res.didYouMean) ? didYouMean : [];


    // Apply extenders to the response
    let extendedResponse = mappedResponse;
    for (const extender of extenders) {
      if (extender.extendGetProductsResult) {
        extendedResponse = await extender.extendGetProductsResult(extendedResponse);
      }
    }

    // Map content from contentLists
    if (contentLists && contentLists.length > 0) {
      const contentListItems = contentLists.flatMap(list => list.items || []);

      // Map pages and articles (both come as type 'page' from Voyado)
      const pages = contentListItems
        .filter(item => item?.type === 'page')
        .map(item => ({
          id: item.key,
          handle: item.link?.replace(/^\/pages\//, '').replace(/^\//, '') || '',
          title: item.title,
          url: item.link,
          description: item.description,
          ticket: item.ticket
        }));

      // Store content in the response for useSearch to pick up
      (extendedResponse as any).contentPages = pages;
      (extendedResponse as any).contentArticles = []; // Articles would be filtered by URL pattern if needed
    }

    return extendedResponse;
  } catch (e) {
    throw new Error(`Error when getting Product results: ${e}`);
  }
}

export default GetProductResults